/* @flow */
import React from 'react';
import classnames from 'classnames/bind';
import { List, Map } from 'immutable';
import { ASC } from 'trustar/utils/constants';
import { truncateText } from 'trustar/utils/utils.js';
import { Select, Spinner } from 'trustar/components';
import { Button, ButtonGroup, IconButton, IconButtonPure } from 'trustar-2.0/components/Button';
import { IconLookup, SearchBar } from 'trustar-2.0/components';
import styled from 'styled-components';
import { connectSelect as connectCompanySelect } from 'modules/companies';
import {noop} from 'rxjs/util/noop';
import s from './Table.styl';
import {Field} from "redux-form/immutable";
const cx = classnames.bind(s);

/**
 * A special select menu that will fetch all companies when focused. This way it
 * will be able to display all companies rather than just whatever page was most
 * recently fetched
 */
const CompanySelect = connectCompanySelect(Select);

const headerHeight = 85;
const controlsHeight = 60;
const tableHeaderHeight = 65;
const tableOffset = headerHeight + controlsHeight + tableHeaderHeight;
export const getTableHeight = () => window.innerHeight - tableOffset;

/* Determine sort arrow for any column which can sort */
export const getSortArrow = (order: string) => {
  const upArrow = (
    <i
      style={{ marginLeft: 2 }}
      className='fa fa-arrow-up'
      aria-hidden='true'
    />
  );
  const downArrow = (
    <i
      style={{ marginLeft: 2 }}
      className='fa fa-arrow-down'
      aria-hidden='true'
    />
  );
  return order === ASC ? upArrow : downArrow;
};

/* Render a title text field */
export const RenderTitleCell = (content: string) => (
  <p className={cx('ColumnTitle')}>{content}</p>
);

export const RenderPatternCell = (pattern: string, showText: boolean) => {
  switch (pattern) {
  case 'COMMUNITY':
    return (
      <span>
        <i key={pattern} className={cx('fa fa-globe', 'green')} />
        {showText && ' Community'}
      </span>
    );
  case 'OPEN':
    return (
      <span>
        <i key={pattern} className={cx('fa fa-rss', 'yellow')} />
        {showText && ' Open Source'}
      </span>
    );
  case 'CLOSED_CONCRETE':
    return (
      <span>
        <i key={pattern} className={cx('fa fa-search-plus', 'gray')} />
        {showText && ' Closed Source'}
      </span>
    );
  case 'INTERNAL':
    return (
      <span>
        <i key={pattern} className={cx('fa fa-lock', 'gray')} />
          Company Internal
        </span>
    );
  case 'OTHER':
    return (
      <span>
        <i key={pattern} className={cx('fa fa-question')} />
          Other
        </span>
    );
  default:
    return null;
  }
};

export const RenderDescriptionCell = (text: string) => (
  <p>{text ? truncateText({ text, length: 100 }) : <em> None.</em>}</p>
);

/* Render a plain text field */
export const RenderPlainCell = (content: string) => <p>{content}</p>;

// Shell component for agregating table column props:
export class SettingsColumn extends React.Component {
  props: {
    identifier: string,
    width: number,
    title: string | React.Element<any>,
    fields: Array<Array<string> | string>,
    render: Function,
  };
  render() {
    return null;
  } // Component merely provides props to <SettingsTable />
}

/* Control Buttons for Table */
export class TableControls extends React.Component {
  props: {
    entity: string,
    viewOptions?: any,
    showFilter?: boolean,
    companyFilterValue?: string,
    enclaveFilterValue?: string,
    rolesFilterValue?: string,
    searchPlaceholder?: string,
    companyFilterPlaceholder?: string,
    enclaveFilterPlaceholder?: string,
    rolesFilterPlaceholder?: string,
    sortOptions?: Array<Object>,
    enclaveOptions?: Map<any, any>,
    rolesOptions?: Map<any, any>,
    customTableInfo?: React.Element<any>,
    handleSort?: Function,
    handleAdd?: Function,
    onChangeSearch?: Function,
    onEnclaveFilterInput?: Function,
    handleFilterCompany?: Function,
    handleFilterEnclave?: Function,
    handleFilterRoles?: Function,
    handleImport?: Function,
    handleExport?: Function | null, // can be null if export not allowed
    showCompanySelect?: boolean,
  };
  render() {
    const {
      entity,
      handleAdd,
      showFilter,
      customTableInfo,
      viewOptions,
      onChangeSearch,
      handleImport,
      handleExport,
      rolesOptions,
      enclaveOptions,
      onEnclaveFilterInput,
      searchPlaceholder,
      showCompanySelect
    } = this.props;
    return (
      <div data-cy={'table'} className={cx('TableControls')}>
        {customTableInfo && (
          <div className={cx('CustomTableInfo')}>{customTableInfo}</div>
        )}
        {onChangeSearch && (
          <SearchBar
            onChange={onChangeSearch}
            className={cx('search')}
            placeholder={searchPlaceholder ? searchPlaceholder : 'Search'}
            value={
              viewOptions &&
              viewOptions.has('query') &&
              viewOptions.get('query')
            }
          />
        )}
        <ButtonGroup style={{marginLeft: '0.5rem'}}>
          {handleImport && (
            <Button
              title='Bulk Import'
              onClick={handleImport}
              fill='outline'
              >
              Bulk Import
            </Button>
          )}
          {handleExport && (
            <Button
              title='Export All'
              onClick={handleExport}
              buttonType={'tertiary'}
            >
              Export All
            </Button>
          )}
          {handleAdd && (
            <Button data-cy={`add-${entity.toLowerCase().replace(/\s/g, '-')}-button`} onClick={handleAdd} >
              Add {entity}
            </Button>
          )}
        </ButtonGroup>
        {showFilter && ( // NOTE: Filter is hidden for non-Admins
          <div className={cx('SortControls')}>
            {showCompanySelect && (
              <CompanySelect
                className={cx('Select')}
                value={this.props.companyFilterValue}
                onChange={this.props.handleFilterCompany}
                placeholder={this.props.companyFilterPlaceholder}
              />
            )}
            {enclaveOptions && (
              <Select
                className={cx('Select')}
                options={enclaveOptions}
                value={this.props.enclaveFilterValue}
                onChange={this.props.handleFilterEnclave}
                onInputChange={(inputValue) => onEnclaveFilterInput ? onEnclaveFilterInput(inputValue) : noop}
                placeholder={this.props.enclaveFilterPlaceholder}
              />
            )}
            {rolesOptions && (
              <Select
                className={cx('RolesSelect')}
                options={rolesOptions}
                value={this.props.rolesFilterValue}
                onChange={this.props.handleFilterRoles}
                placeholder={this.props.rolesFilterPlaceholder}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

const StyledTableCellButtonGroup = styled(ButtonGroup)`
  padding-top: 5px;
`;


/* Manage Buttons for Table: supports Edit, Delete, and Deactivate (users) */
export class TableManageButtons extends React.Component {
  props: {
    id: string,
    email?: string,
    guid?: string,
    handleEdit?: Function,
    handleDelete?: Function,
    handleDeactivate?: Function,
  };

  render() {
    const {
      guid,
      id,
      email,
      handleEdit,
      handleDelete,
      handleDeactivate
    } = this.props;
    let identifier = guid == null ? id : guid;
    return (
      <StyledTableCellButtonGroup>
        {handleEdit && (
          <IconButtonPure
            title='Edit'
            cyid={'editItem'}
            transparent
            fill='none'
            small
            glowHover={true}
            icon={IconLookup.PENCIL}
            onClick={() => handleEdit(identifier)}
          />
        )}
        {handleDeactivate && (
          <IconButtonPure
            title='Activate / Deactivate'
            transparent
            cyid={'toggleActiveState'}
            glowHover={true}
            fill='none'
            small
            icon={IconLookup.USER_MINUS}
            onClick={() => handleDeactivate(email)}
          />
        )}
        {handleDelete && (
          <IconButtonPure
            title='Delete'
            cyid={'deleteItem'}
            transparent
            glowHover={true}
            fill='none'
            small
            icon={IconLookup.TRASH2}
            onClick={() => handleDelete(identifier)}
          />
        )}
      </StyledTableCellButtonGroup>
    );
  }
}

/* Generic Table Component used for Settings Pages:
 * Currently used for: Users, Companies, Enlaves Tables
 *
 * Table Component API:
 *
 * All <TableColumns /> have these props:
 *
 * <props>:     <description>
 * identifier:  Unique idetifier for column
 * width:       Width of the column
 * title:       A string or JSX rendered as the column title
 * fields:      Key paths of the fields which become arguments in the render method
 * render:      Cell render method which recievs fields[] props as arugments
 *
 * The SettingsTable receives a prop {content} which contains the row data
 * to be mapped over (e.g. list of users). The component aggregates the
 * cell mapping functions from the TableColumns (above ^), which are present
 * here as props.children. Then it maps over the {content} applying the
 * logic from the TableColumns.
 *
 **/
export class SettingsTable extends React.Component {
  props: {
    children: Object,
    content: List<any>,
    tableHeight: number | string, // px or 'auto'
    onScroll?: Function,
    scrollLoading?: boolean,
  };

  container: $TODO;

  getScrollContainer = () => this.container;

  render() {
    const { content, children, tableHeight } = this.props;
    const propsTemplate = children.map(c => c.props);
    return (
      <div data-cy={'settings-table'} className={cx('SettingsTable')}>
        <div className={cx('HeaderRow')}>
          {propsTemplate.map(t => {
            return (
              <div
                key={JSON.stringify(t.fields) + t.identifier}
                className={cx('HeaderCell')}
                style={{ width: t.width }}>
                {t.title}
              </div>
            );
          })}
          {this.props.scrollLoading && <Spinner />}
        </div>
        <div
          ref={el => {
            this.container = el;
          }}
          onScroll={this.props.onScroll}
          className={cx('TableContainer')}
          style={{ height: tableHeight }}>
          <div className={cx('TableContent')}>
            {content.map(item => {
              // Use guid or id for unique ID for cell element
              const uniqueItemId = item.get('guid', item.get('id'));
              return (
                <div data-cy={'table-row'} key={uniqueItemId} className={cx('TableRow')}>
                  {propsTemplate.map(t => {
                    return (
                      <div
                        key={uniqueItemId + t.identifier}
                        className={cx('Cell')}
                        style={{ width: t.width }}>
                        {t.render(
                          ...t.fields.map(f =>
                            Array.isArray(f) ? item.getIn(f) : item.get(f)
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./web_modules/trustar/components/Table.js